import type { OrderDetails } from './order.types'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { stateKeys } from '@/entities/state/model'
import { updateAcknowledgeOrder, updateOrderStatus } from '../api/order.api'
import { orderKeys } from './order.queryKeys'

export interface UpdateOrderStatusParams {
  orderId: string
  newStatus: string
  lastUpdateTime?: string
  verificationData?: {
    verificationCode?: string
    verificationErrorCode?: string
  }
}

/**
 * Hook for updating order status with optimistic updates
 *
 * This hook can be used in any component that needs to update order status
 * and provides optimistic updates for a better user experience.
 */
export function useOrderStatusMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params: UpdateOrderStatusParams) => updateOrderStatus<OrderDetails>(params),

    // Optimistic update
    onMutate: async (variables) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: orderKeys.orders() })

      // Snapshot the previous value
      const previousOrders = queryClient.getQueryData<OrderDetails[]>(orderKeys.orders())

      // Optimistically update to the new value
      if (previousOrders) {
        queryClient.setQueryData<OrderDetails[]>(
          orderKeys.orders(),
          previousOrders.map(order =>
            order.orderId === variables.orderId
              ? { ...order, status: variables.newStatus }
              : order,
          ),
        )
      }

      return { previousOrders }
    },

    // Update with the response data
    onSuccess: (data: OrderDetails, variables) => {
      // Get the current orders data
      const currentOrders = queryClient.getQueryData<OrderDetails[]>(orderKeys.orders())

      if (currentOrders) {
        // Update the order with the response data
        queryClient.setQueryData<OrderDetails[]>(
          orderKeys.orders(),
          currentOrders.map(order =>
            order.orderId === variables.orderId
              ? {
                  ...order,
                  ...data, // Update with all the response data
                }
              : order,
          ),
        )
      }
    },

    // If the mutation fails, use the context returned from onMutate to roll back
    // and check if we need to invalidate state config
    onError: (error: any, _variables, context: any) => {
      // Roll back optimistic update
      if (context?.previousOrders) {
        queryClient.setQueryData(orderKeys.orders(), context.previousOrders)
      }

      // Check if error indicates invalid state transition or outdated state config
      const isStateConfigError = (
        // Check for specific error codes that might indicate state template change
        error?.status === 400 && (
          error?.data?.errorType === 'ValidationError'
          && error?.data?.errorMessages?.errorDescription?.includes('support state')
        )
      )

      // If this is a state config error and we haven't already invalidated
      if (isStateConfigError && !context?.stateConfigInvalidated) {
        // Invalidate state config to force refresh
        queryClient.invalidateQueries({ queryKey: stateKeys.stateConfig() })

        // Mark that we've already invalidated to prevent multiple invalidations
        // during retries of the same failed mutation
        if (context) {
          context.stateConfigInvalidated = true
        }

        // Log the error for debugging
        console.warn('Detected possible state template change, refreshing state config')
      }
    },

    // Always refetch after error or success
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: orderKeys.orders() })
      queryClient.invalidateQueries({ queryKey: orderKeys.waitTime() })
    },
  })
}

/**
 * Hook for acknowledging an order
 *
 * This mutation sets the `isAcknowledged` field to true for the specified order.
 */
export function useAcknowledgeOrderMutation() {
  return useMutation({
    mutationFn: async ({ orderId, lastUpdateTime }: { orderId: string, lastUpdateTime?: string }) => {
      return updateAcknowledgeOrder<OrderDetails>({ orderId, lastUpdateTime })
    },
    onError: (error, variables) => {
      console.error(`Failed to acknowledge order: ${variables.orderId}`, error)
    },
    retry: 3,
  })
}
